import React, { FC, useState } from "react";
import { useFormikContext } from "formik";
import { Box, ButtonBase, Collapse, FormControl, TextareaAutosize, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { InputPanel } from "./InputPanel";
import { PanelWrapper } from "./PanelWrapper";
import { BriefAssetFile, BriefAssetKind } from "./BriefAssetFile";

interface Props {
    /** Upload endpoint per kind; omitted = files are shown but not editable. */
    uploadUrlFor?: (kind: BriefAssetKind) => string | undefined;
    /** Products to collect disclaimers for (the brief); omit on brand settings. */
    products?: { desireid: number; title: string }[];
    disabled?: boolean;
    heading?: string;
    /** Field-name prefix: '' for the brand's own set, 'rmn_' for the RMN's on a brief. */
    prefix?: string;
    /** Whose values these are, for the "saved to…" hint. */
    ownerLabel?: string;
    /** Heading becomes a toggle (the brief form); fields stay mounted while closed. */
    collapsible?: boolean;
    defaultExpanded?: boolean;
}

const ASSET_FIELDS = [
    "brief_tagline",
    "brief_logo",
    "brief_colors",
    "brief_colors_file",
    "brief_font",
    "brief_font_file",
];

// One disclaimers box per product. The form holds product_disclaimers as a
// { [desireid]: text } object and every write replaces it whole — Formik's
// setIn would turn a numeric path segment into an array index.
const DisclaimerField: FC<{ desireid: number; title: string }> = ({ desireid, title }) => {
    const { values, setFieldValue } = useFormikContext<any>();
    const map: Record<string, string> = values.product_disclaimers || {};
    return (
        <PanelWrapper
            body={`Disclaimers — ${title}`}
            footer="Legal or claims language that must appear with, or be said in, the video for this product."
        >
            <FormControl fullWidth>
                <TextareaAutosize
                    minRows={2}
                    maxRows={6}
                    value={map[desireid] || ""}
                    onChange={(e) =>
                        setFieldValue("product_disclaimers", { ...map, [desireid]: e.target.value })
                    }
                    className="w-full p-2 border border-surface-2 bg-surface-2 rounded-md focus:bg-surface focus:border-accent outline-0 focus:outline-0 resize-none"
                />
            </FormControl>
        </PanelWrapper>
    );
};

/**
 * TDC-1722 brief brand assets. The same inputs on Brand Settings (brand fields
 * only) and on the brief (plus per-product disclaimers), so the values a brand
 * enters in one place are the ones the other shows.
 */
export const BriefBrandAssetsFields: FC<Props> = ({
    uploadUrlFor,
    products,
    disabled,
    heading,
    prefix = "",
    ownerLabel = "the brand",
    collapsible = false,
    defaultExpanded = false,
}) => {
    const { values } = useFormikContext<any>();
    const [open, setOpen] = useState(!collapsible || defaultExpanded);
    const urlFor = (kind: BriefAssetKind) => (uploadUrlFor ? uploadUrlFor(kind) : undefined);

    // Shown on the closed header so a pre-filled section isn't mistaken for empty.
    const disclaimerMap: Record<string, string> = values.product_disclaimers || {};
    const total = ASSET_FIELDS.length + (products || []).length;
    const filled =
        ASSET_FIELDS.filter((key) => !!values[`${prefix}${key}`]).length +
        (products || []).filter((product) => !!disclaimerMap[product.desireid]).length;

    const fields = (
        <Box className="flex flex-col w-full gap-6">
            <InputPanel
                name={`${prefix}brief_tagline`}
                body="Brand tagline"
                footer={`Saved to ${ownerLabel}, so the next brief starts with it.`}
            />
            <BriefAssetFile
                name={`${prefix}brief_logo`}
                kind="logo"
                body="Logo for videos"
                footer="The logo to use in video content — can differ from the brand's profile logo. PNG, JPG, SVG or PDF."
                uploadUrl={urlFor("logo")}
                disabled={disabled}
            />
            <InputPanel
                name={`${prefix}brief_colors`}
                body="Brand colors"
                rows={2}
                footer="For Example: #0A2540 navy, #FFB000 gold."
            />
            <BriefAssetFile
                name={`${prefix}brief_colors_file`}
                kind="colors"
                body="Brand colors file"
                footer="A palette or style guide page. PDF, PNG, JPG, TXT or ASE."
                uploadUrl={urlFor("colors")}
                disabled={disabled}
            />
            <InputPanel name={`${prefix}brief_font`} body="Brand font" footer="For Example: Helvetica Neue Bold for titles." />
            <BriefAssetFile
                name={`${prefix}brief_font_file`}
                kind="font"
                body="Brand font file"
                footer="TTF, OTF, WOFF, WOFF2 or a ZIP of the family."
                uploadUrl={urlFor("font")}
                disabled={disabled}
            />
            {(products || []).map((product) => (
                <DisclaimerField key={product.desireid} desireid={product.desireid} title={product.title} />
            ))}
        </Box>
    );

    if (!collapsible) {
        return (
            <Box className="flex flex-col w-full gap-6">
                {heading && (
                    <div className="font-[900] text-[18px] leading-[132%] tracking-normal">{heading}</div>
                )}
                {fields}
            </Box>
        );
    }

    return (
        <Box className="flex flex-col w-full border border-border-token rounded-md">
            <ButtonBase
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
                className="!flex !justify-between !items-center !w-full !px-4 !py-3 !text-left"
            >
                <Box className="flex flex-col">
                    <span className="font-[900] text-[18px] leading-[132%] tracking-normal">{heading}</span>
                    <Typography variant="body2" className="!text-text-2">
                        {filled} of {total} filled
                    </Typography>
                </Box>
                <ExpandMoreIcon
                    className="transition-transform"
                    style={{ transform: open ? "rotate(180deg)" : "none" }}
                />
            </ButtonBase>
            <Collapse in={open}>
                <Box className="px-4 pb-4 pt-2">{fields}</Box>
            </Collapse>
        </Box>
    );
};
